import type { NavItem, NavSection } from './nav-config';
import type { KhoaDich } from '../../i18n';

/**
 * Hàm dịch truyền vào từ chỗ gọi.
 *
 * Các hàm trong tệp này là **hàm thuần**, không gọi hook — nhờ vậy test được
 * không cần React (đó là lý do chúng nằm riêng, xem ADR-UI-0009). Nhưng nhãn
 * giờ nằm trong catalog, nên phải nhận hàm dịch qua tham số: lọc theo khoá thô
 * thì gõ `chung tu` không khớp `nav.muc.quanLyChungTu`, và tìm kiếm im lặng.
 */
export type HamDich = (khoa: KhoaDich) => string;

/** Tên nhóm hiện ra: `nhanNhomThang` (nhóm sinh lúc chạy, ADR-0096) thắng, rồi mới tra catalog. */
export function tenNhom(section: NavSection, dich: HamDich): string {
  return section.nhanNhomThang ?? dich(section.khoaNhom as KhoaDich);
}

/** Nhãn một mục: `nhanThang` (sinh lúc chạy, không dịch được) thắng, rồi mới tra catalog — cùng luật `nhanMuc()` ở `Sidebar.tsx`. */
function nhanMuc(item: NavItem, dich: HamDich): string {
  return item.nhanThang ?? dich(item.khoa);
}

/**
 * Tìm kiếm trong cây điều hướng — bỏ dấu, không phân biệt chữ hoa.
 *
 * Vì sao phải bỏ dấu: người dùng gõ nhanh trong ô tìm thì gõ `tri thuc`, `chung
 * tu`, `nhat ky` chứ không bỏ công gõ `tri thức`, `chứng từ`, `nhật ký`. Nếu
 * khớp theo đúng chuỗi có dấu thì ô tìm im lặng đúng vào lúc người dùng cần
 * nó nhất, và họ kết luận là "tìm không ra" chứ không nghĩ là do thiếu dấu.
 *
 * Cách làm: `NFD` tách chữ khỏi dấu thành hai mã riêng, rồi xoá dải dấu
 * `U+0300–U+036F`. `đ`/`Đ` **không** phải chữ có dấu — nó là một ký tự riêng,
 * `NFD` không tách được — nên phải thay tay, nếu không `dinh danh` sẽ không bao
 * giờ khớp `Định danh`.
 *
 * Chữ tiếng Anh đi qua hàm này **không đổi** (ASCII không có dấu kết hợp), nên
 * ô tìm dùng đúng một đường cho cả hai ngôn ngữ.
 */
export function boDau(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
}

/**
 * Từ khoá đã chuẩn hoá, tách theo khoảng trắng.
 *
 * Tách từ để `ky thuat nhat` vẫn tìm ra `Nhật ký thao tác`: người dùng nhớ các
 * từ trong nhãn nhưng không nhớ đúng thứ tự. Mọi từ đều phải khớp (AND), nếu
 * không thì gõ thêm từ lại ra nhiều kết quả hơn — ngược với trực giác.
 */
export function tachTuKhoa(tuKhoa: string): string[] {
  return boDau(tuKhoa.trim()).split(/\s+/).filter(Boolean);
}

/** Nhãn có chứa **mọi** từ khoá hay không. */
export function khopNhan(nhan: string, tu: readonly string[]): boolean {
  if (tu.length === 0) return true;
  const chuan = boDau(nhan);
  return tu.every((t) => chuan.includes(t));
}

/**
 * Một đoạn của nhãn sau khi cắt theo chỗ khớp, để tô sáng.
 *
 * Tô sáng là thứ nói cho người dùng biết **vì sao** một dòng còn trong danh
 * sách — thiếu nó thì kết quả tìm trông như một danh sách bị cắt ngẫu nhiên.
 */
export interface DoanNhan {
  chu: string;
  khop: boolean;
}

/**
 * Cắt nhãn thành các đoạn khớp / không khớp.
 *
 * Chỉ số tìm được trên chuỗi **đã bỏ dấu**, nhưng đoạn trả về lấy từ chuỗi
 * **gốc**: hai chuỗi cùng độ dài vì `boDau` chỉ xoá dấu kết hợp (`NFD` tách
 * thành ký tự riêng rồi xoá) và đổi `đ`→`d` một-đổi-một, nên chỉ số dùng chung
 * được. Nhờ vậy ô tìm tô sáng mà chữ hiện ra vẫn đủ dấu.
 */
export function cheDoanNhan(nhan: string, tu: readonly string[]): DoanNhan[] {
  if (tu.length === 0) return [{ chu: nhan, khop: false }];

  const chuan = boDau(nhan);
  // Đánh dấu từng ký tự có thuộc một chỗ khớp nào không, rồi mới gộp đoạn —
  // làm vậy thì các chỗ khớp chồng nhau (gõ `nhat ky nhat`) tự hợp lại thành
  // một đoạn, không sinh ra đoạn rỗng hay đoạn lồng nhau.
  const duocTo = new Array<boolean>(nhan.length).fill(false);

  for (const t of tu) {
    let tu_vi = chuan.indexOf(t);
    while (tu_vi !== -1) {
      for (let i = tu_vi; i < tu_vi + t.length && i < duocTo.length; i += 1) duocTo[i] = true;
      tu_vi = chuan.indexOf(t, tu_vi + 1);
    }
  }

  const doan: DoanNhan[] = [];
  for (let i = 0; i < nhan.length; i += 1) {
    const khop = duocTo[i]!;
    const cuoi = doan[doan.length - 1];
    if (cuoi && cuoi.khop === khop) cuoi.chu += nhan[i];
    else doan.push({ chu: nhan[i]!, khop });
  }
  return doan;
}

/**
 * Lọc cây điều hướng theo từ khoá.
 *
 * Luật: **mục cha khớp thì giữ nguyên cả cụm con.** Gõ `rpa` mà chỉ còn mục cha
 * `Quản lý Project & Workflow` trống rỗng thì người dùng không tới được màn nào;
 * giữ cả cụm thì một lần gõ là thấy hết việc liên quan. Mục cha không khớp thì
 * chỉ giữ những mục con khớp, và bỏ hẳn nếu không con nào khớp — mục cha trống
 * là một ngăn mở ra chẳng có gì, đúng cái bẫy §4.3 đã dặn.
 */
export function locCayDieuHuong(
  sections: readonly NavSection[],
  tuKhoa: string,
  dich: HamDich,
): NavSection[] {
  const tu = tachTuKhoa(tuKhoa);
  if (tu.length === 0) return sections as NavSection[];

  return sections
    .map((section) => ({
      khoaNhom: section.khoaNhom,
      nhanNhomThang: section.nhanNhomThang,
      phamVi: section.phamVi,
      // Tên nhóm cũng được khớp: gõ `tri thuc` thì cả nhóm `TRI THỨC AI` hiện
      // ra, kể cả mục con không mang chữ đó trong nhãn.
      items: khopNhan(tenNhom(section, dich), tu)
        ? section.items
        : section.items.flatMap<NavItem>((item) => {
            const nhan = nhanMuc(item, dich);
            if (!item.children) return khopNhan(nhan, tu) ? [item] : [];
            if (khopNhan(nhan, tu)) return [item];
            const con = item.children.filter((c) => khopNhan(nhanMuc(c, dich), tu));
            return con.length > 0 ? [{ ...item, children: con }] : [];
          }),
    }))
    .filter((section) => section.items.length > 0);
}

/** Mọi mục lá còn lại sau khi lọc, theo đúng thứ tự thấy trên màn hình. */
export function ganLa(sections: readonly NavSection[]): NavItem[] {
  return sections.flatMap((s) =>
    s.items.flatMap<NavItem>((item) => (item.children ? item.children : item.href ? [item] : [])),
  );
}
